/**
 * Member directory (AM-01..09). Searchable/filterable list over v_member_list
 * (RLS-isolated) → member detail side-drawer. Money/dates localized.
 * Task 1.2: in-page tabs — Members directory + Payments tab.
 */
import type { Gender, MembershipStatus, PlanType, types } from '@gym/shared';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { UserPlus, Users } from 'lucide-react';
import { type ReactNode, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../components/ui/Button.js';
import { EmptyState } from '../../components/ui/Feedback.js';
import { Field, Input, Select } from '../../components/ui/Field.js';
import { PageHeader } from '../../components/ui/Layout.js';
import { Modal } from '../../components/ui/Modal.js';
import { useFormatters } from '../../components/ui/Money.js';
import { StatusPill, statusTone } from '../../components/ui/StatusPill.js';
import { Table, TableSkeleton, TableWrap, TD, TH, THead, TR } from '../../components/ui/Table.js';
import { Tabs } from '../../components/ui/Tabs.js';
import { toast } from '../../components/ui/Toaster.js';
import { useAuth } from '../../lib/auth.js';
import { supabase } from '../../lib/supabase.js';

import { MemberDetailDrawer } from './MemberDetailDrawer.js';
import { type ExpirySort, MemberFiltersToolbar } from './MemberFiltersToolbar.js';
import { PaymentsTab } from './PaymentsTab.js';

export interface MemberRow {
  id: string;
  first_name: string;
  last_name: string;
  email: string | null;
  phone: string | null;
  gender: string | null;
  date_of_birth: string | null;
  join_date: string;
  status: string;
  age_group: string | null;
  longevity_months: number | null;
  active_membership_id: string | null;
  plan_id: string | null;
  plan_type: PlanType | null;
  membership_status: MembershipStatus | null;
  end_date: string | null;
  credits_remaining: number | null;
}

const STATUS_FILTERS: (MembershipStatus | 'all')[] = [
  'all',
  'active',
  'expired',
  'frozen',
  'pending',
  'cancelled',
];

export function MembersPage(): ReactNode {
  const { t } = useTranslation();
  const fmt = useFormatters();
  const { claims } = useAuth();
  const tenantId = claims?.tenant_id ?? '';

  const [tab, setTab] = useState<'members' | 'payments'>('members');

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<MembershipStatus | 'all'>('all');
  const [plan, setPlan] = useState<PlanType | 'all'>('all');
  const [ageGroup, setAgeGroup] = useState<types.AgeGroup | 'all'>('all');
  const [gender, setGender] = useState<Gender | 'all'>('all');
  const [expirySort, setExpirySort] = useState<ExpirySort>('none');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [chargeMemberId, setChargeMemberId] = useState<string | null>(null);

  function cycleExpirySort(): void {
    setExpirySort((prev) => (prev === 'none' ? 'soonest' : prev === 'soonest' ? 'latest' : 'none'));
  }

  const query = useQuery({
    queryKey: ['members', tenantId, search, status, plan, ageGroup, gender, expirySort],
    enabled: !!tenantId,
    placeholderData: keepPreviousData,
    queryFn: async (): Promise<MemberRow[]> => {
      let q = supabase
        .from('v_member_list')
        .select(
          'id, first_name, last_name, email, phone, gender, date_of_birth, join_date, status, age_group, longevity_months, active_membership_id, plan_id, plan_type, membership_status, end_date, credits_remaining',
        )
        .eq('tenant_id', tenantId);
      if (search.trim()) {
        const term = `%${search.trim()}%`;
        q = q.or(
          `first_name.ilike.${term},last_name.ilike.${term},phone.ilike.${term},email.ilike.${term}`,
        );
      }
      if (status !== 'all') q = q.eq('membership_status', status);
      if (plan !== 'all') q = q.eq('plan_type', plan);
      if (ageGroup !== 'all') q = q.eq('age_group', ageGroup);
      if (gender !== 'all') q = q.eq('gender', gender);
      if (expirySort === 'soonest') {
        q = q.order('end_date', { ascending: true, nullsFirst: false });
      } else if (expirySort === 'latest') {
        q = q.order('end_date', { ascending: false, nullsFirst: false });
      } else {
        q = q.order('join_date', { ascending: false });
      }
      const { data, error } = await q.limit(100);
      if (error) throw error;
      return (data ?? []) as unknown as MemberRow[];
    },
  });

  const rows = useMemo(() => query.data ?? [], [query.data]);
  const selected = useMemo(() => rows.find((r) => r.id === selectedId) ?? null, [rows, selectedId]);

  return (
    <div className="grid gap-5">
      <PageHeader
        title={t('members.title')}
        subtitle={t('members.subtitle')}
        actions={
          tab === 'members' ? (
            <Button
              icon={<UserPlus className="h-4 w-4" />}
              variant="brand"
              onClick={() => setCreateOpen(true)}
            >
              {t('members.add')}
            </Button>
          ) : undefined
        }
      />

      <Tabs
        value={tab}
        onValueChange={(v) => setTab(v as 'members' | 'payments')}
        items={[
          { value: 'members', label: t('members.tabs.members') },
          { value: 'payments', label: t('members.tabs.payments') },
        ]}
      />

      {tab === 'members' ? (
        <>
          <MemberFiltersToolbar
            search={search}
            onSearch={setSearch}
            status={status}
            onStatus={setStatus}
            statusOptions={STATUS_FILTERS}
            plan={plan}
            onPlan={setPlan}
            ageGroup={ageGroup}
            onAgeGroup={setAgeGroup}
            gender={gender}
            onGender={setGender}
            expirySort={expirySort}
            onCycleExpirySort={cycleExpirySort}
          />

          <div className="overflow-hidden rounded-xl border border-border bg-surface">
            <TableWrap>
              <Table>
                <THead>
                  <tr>
                    <TH>{t('members.fields.firstName')}</TH>
                    <TH>{t('members.fields.phone')}</TH>
                    <TH>{t('memberships.plan')}</TH>
                    <TH>{t('memberships.title')}</TH>
                    <TH numeric>{t('memberships.endDate')}</TH>
                  </tr>
                </THead>
                {query.isLoading ? (
                  <TableSkeleton rows={8} cols={5} />
                ) : rows.length === 0 ? null : (
                  <tbody>
                    {rows.map((m) => (
                      <TR key={m.id} clickable onClick={() => setSelectedId(m.id)}>
                        <TD>
                          <div className="flex items-center gap-2.5">
                            <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-surface-2 text-xs font-semibold text-muted">
                              {m.first_name?.[0]}
                              {m.last_name?.[0]}
                            </span>
                            <div className="min-w-0">
                              <div className="truncate font-medium text-fg">
                                {m.first_name} {m.last_name}
                              </div>
                              {m.email ? (
                                <div className="truncate text-xs text-muted">{m.email}</div>
                              ) : null}
                            </div>
                          </div>
                        </TD>
                        <TD className="text-muted">{m.phone ?? '—'}</TD>
                        <TD className="text-muted">
                          {m.plan_type
                            ? t(`plans.type.${m.plan_type}`, { defaultValue: m.plan_type })
                            : '—'}
                        </TD>
                        <TD>
                          {m.membership_status ? (
                            <StatusPill
                              status={m.membership_status}
                              tone={statusTone(m.membership_status)}
                              label={t(`memberships.status.${m.membership_status}`, {
                                defaultValue: m.membership_status,
                              })}
                            />
                          ) : (
                            <span className="text-faint">{t('memberships.none')}</span>
                          )}
                        </TD>
                        <TD numeric className="text-muted">
                          {m.end_date ? fmt.date(m.end_date) : '—'}
                        </TD>
                      </TR>
                    ))}
                  </tbody>
                )}
              </Table>
            </TableWrap>
            {!query.isLoading && rows.length === 0 ? (
              <div className="p-6">
                <EmptyState
                  icon={Users}
                  title={t('members.empty')}
                  hint={
                    search ||
                    status !== 'all' ||
                    plan !== 'all' ||
                    ageGroup !== 'all' ||
                    gender !== 'all'
                      ? t('members.emptyFiltered')
                      : t('members.emptyHint')
                  }
                />
              </div>
            ) : null}
          </div>

          <MemberDetailDrawer
            member={selected}
            open={!!selected}
            onOpenChange={(o) => !o && setSelectedId(null)}
            onRecordPayment={(id) => {
              setChargeMemberId(id);
              setSelectedId(null);
              setTab('payments');
            }}
          />

          <NewMemberModal tenantId={tenantId} open={createOpen} onOpenChange={setCreateOpen} />
        </>
      ) : (
        <PaymentsTab tenantId={tenantId} initialMemberId={chargeMemberId} />
      )}
    </div>
  );
}

const GENDERS: Gender[] = ['unspecified', 'male', 'female', 'other'];
const ACQUISITION_SOURCES = ['walk_in', 'referral', 'social', 'campaign', 'other'] as const;

function NewMemberModal({
  tenantId,
  open,
  onOpenChange,
}: {
  tenantId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): ReactNode {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [gender, setGender] = useState<Gender>('unspecified');
  const [dob, setDob] = useState('');
  const [acquisitionSource, setAcquisitionSource] = useState('');

  function reset(): void {
    setFirstName('');
    setLastName('');
    setEmail('');
    setPhone('');
    setGender('unspecified');
    setDob('');
    setAcquisitionSource('');
  }

  const create = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.from('members').insert({
        tenant_id: tenantId,
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        email: email.trim() || null,
        phone: phone.trim() || null,
        gender,
        date_of_birth: dob || null,
        acquisition_source: acquisitionSource || null,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success(t('members.created'), `${firstName} ${lastName}`.trim());
      void qc.invalidateQueries({ queryKey: ['members'] });
      reset();
      onOpenChange(false);
    },
    onError: (e: Error) => toast.error(t('common.error'), e.message),
  });

  return (
    <Modal
      open={open}
      onOpenChange={(o) => {
        if (!o) reset();
        onOpenChange(o);
      }}
      title={t('members.add')}
      footer={
        <>
          <Button
            variant="secondary"
            onClick={() => onOpenChange(false)}
            disabled={create.isPending}
          >
            {t('common.cancel')}
          </Button>
          <Button
            onClick={() => create.mutate()}
            loading={create.isPending}
            disabled={!firstName.trim() || !lastName.trim()}
          >
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="grid gap-3">
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label={t('members.fields.firstName')} required>
            {(id) => (
              <Input
                id={id}
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                autoFocus
              />
            )}
          </Field>
          <Field label={t('members.fields.lastName')} required>
            {(id) => (
              <Input id={id} value={lastName} onChange={(e) => setLastName(e.target.value)} />
            )}
          </Field>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label={t('members.fields.email')}>
            {(id) => (
              <Input
                id={id}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            )}
          </Field>
          <Field label={t('members.fields.phone')}>
            {(id) => <Input id={id} value={phone} onChange={(e) => setPhone(e.target.value)} />}
          </Field>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label={t('members.fields.gender')}>
            {(id) => (
              <Select id={id} value={gender} onChange={(e) => setGender(e.target.value as Gender)}>
                {GENDERS.map((g) => (
                  <option key={g} value={g}>
                    {t(`members.gender.${g}`, { defaultValue: g })}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label={t('members.fields.dob')}>
            {(id) => (
              <Input id={id} type="date" value={dob} onChange={(e) => setDob(e.target.value)} />
            )}
          </Field>
        </div>
        <Field label={t('members.fields.acquisitionSource')}>
          {(id) => (
            <Select
              id={id}
              value={acquisitionSource}
              onChange={(e) => setAcquisitionSource(e.target.value)}
            >
              <option value="">—</option>
              {ACQUISITION_SOURCES.map((s) => (
                <option key={s} value={s}>
                  {t(`members.acquisitionSource.${s}`)}
                </option>
              ))}
            </Select>
          )}
        </Field>
      </div>
    </Modal>
  );
}
